import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";
import { z } from "zod";

// Enum options extracted from Ruby StrategyConcern::Enums

export const typeOptions = [
  { value: "Strategy", label: "Strategy" },
  { value: "Portfolio", label: "Portfolio" },
  { value: "AbsolutePortfolio", label: "Absolute Portfolio" },
];

export const instrumentOptions = [
  { value: "0", label: "Bond" },
  { value: "1", label: "Stock" },
  { value: "2", label: "Derivative" },
  { value: "3", label: "Loan" },
  { value: "4", label: "Fund" },
  { value: "5", label: "Index" },
  { value: "6", label: "Repo" },
  { value: "7", label: "Reverse Repo" },
  { value: "8", label: "Convertible Bond" },
  { value: "9", label: "Physical Metal" },
  { value: "10", label: "Cash" },
  { value: "11", label: "Right" },
  { value: "12", label: "Partnership Share" },
  { value: "13", label: "Credit Default Index" },
  { value: "14", label: "Vvpr" },
  { value: "15", label: "Na" },
  { value: "16", label: "Various" },
  { value: "17", label: "Etf" },
  { value: "18", label: "Offset Cash" },
  { value: "19", label: "Prime Fidelite" },
  { value: "20", label: "Sci" },
];

export const statusOptions = [
  { value: "0", label: "Draft" },
  { value: "1", label: "Waiting Approval" },
  { value: "2", label: "Listed" },
  { value: "3", label: "Hidden" },
  { value: "4", label: "Deprecated" },
  { value: "5", label: "Onboarding" },
  { value: "6", label: "Created By ETL" },
  { value: "7", label: "Under Investigation" },
  { value: "8", label: "Published" },
];

export const assetClassOptions = [
  { value: "0", label: "Fx" },
  { value: "1", label: "Equity" },
  { value: "2", label: "Fixed Income" },
  { value: "3", label: "Commodities" },
  { value: "4", label: "Cash" },
  { value: "5", label: "Credit" },
  { value: "6", label: "Multi Asset" },
  { value: "7", label: "Na" },
  { value: "8", label: "Cryptocurrency" },
  { value: "9", label: "Real Estate" },
  { value: "10", label: "Private Equity" },
  { value: "11", label: "Private Debt" },
  { value: "12", label: "Infrastructure" },
];

export const derivativeTypeOptions = [
  { value: "0", label: "European Call" },
  { value: "1", label: "European Put" },
  { value: "2", label: "Frn" },
  { value: "3", label: "Clo" },
  { value: "4", label: "Equity Linked Certificate" },
  { value: "5", label: "Mbs" },
  { value: "6", label: "Abs" },
  { value: "7", label: "Cfd" },
  { value: "8", label: "Forward" },
  { value: "9", label: "Future" },
  { value: "10", label: "Cmbs" },
  { value: "11", label: "Cd" },
  { value: "12", label: "Cds" },
  { value: "13", label: "American Call" },
  { value: "14", label: "American Put" },
  { value: "15", label: "Swap" },
  { value: "16", label: "Cmo" },
  { value: "17", label: "Interest Rate Swap" },
  { value: "18", label: "Total Return Swap" },
  { value: "19", label: "Warrant" },
  { value: "20", label: "Equity Swap" },
  { value: "21", label: "Na" },
  { value: "22", label: "Various" },
];

export const returnTypeOptions = [
  { value: "0", label: "Excess Return" },
  { value: "1", label: "Total Return" },
  { value: "2", label: "Price Return" },
];

export const frequencyOptions = [
  { value: "0", label: "Daily" },
  { value: "1", label: "Weekly" },
  { value: "2", label: "Monthly" },
  { value: "3", label: "Quarterly" },
  { value: "4", label: "Yearly" },
];

export const bondTypeOptions = [
  { value: "0", label: "Basic" },
  { value: "1", label: "Convertible" },
  { value: "2", label: "Coco" },
  { value: "3", label: "Callable" },
  { value: "4", label: "Perpetual" },
];

export const interestRateTypeOptions = [
  { value: "fixed", label: "Fixed" },
  { value: "floating", label: "Floating" },
  { value: "variable", label: "Variable" },
  { value: "inflation_linked", label: "Inflation Linked" },
];

export const filterStrategySchema = z.object({
  type_contains: z.string().nullable().optional(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  company_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  instrument_contains: z.string().nullable().optional(),
  status_contains: z.string().nullable().optional(),
  asset_class_contains: z.string().nullable().optional(),
  derivative_type_contains: z.string().nullable().optional(),
  currency_contains: z.string().nullable().optional(),
  return_type_contains: z.string().nullable().optional(),
  issuer_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  yahoo_ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  provider_compo_code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  provider_fixings_code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  is_benchmark: z.string().optional(),
  provider_config_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  created_at_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  created_at_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
});

export type FilterStrategySchema = z.infer<typeof filterStrategySchema>;

export const fields: Field[] = [
  {
    name: "type_contains",
    label: "Type",
    type: "select",
    useTranslation: false,
    options: typeOptions,
  },
  {
    name: "isin_contains",
    label: "ISIN",
    type: "input",
  },
  {
    name: "name_contains",
    label: "Name",
    type: "input",
  },
  {
    name: "company_name_contains",
    label: "Company name",
    type: "input",
  },
  {
    name: "instrument_contains",
    label: "Instrument",
    type: "select",
    useTranslation: false,
    options: instrumentOptions,
  },
  {
    name: "status_contains",
    label: "Status",
    type: "select",
    useTranslation: false,
    options: statusOptions,
  },
  {
    name: "asset_class_contains",
    label: "Asset Class",
    type: "select",
    useTranslation: false,
    options: assetClassOptions,
  },
  {
    name: "derivative_type_contains",
    label: "Derivative Type",
    type: "select",
    useTranslation: false,
    options: derivativeTypeOptions,
  },
  {
    name: "currency_contains",
    label: "Currency",
    type: "autocomplete",
    options: createCurrencyOption(),
  },
  {
    name: "return_type_contains",
    label: "Return Type",
    type: "select",
    useTranslation: false,
    options: returnTypeOptions,
  },
  {
    name: "issuer_contains",
    label: "Issuer",
    type: "asyncAutocomplete",
    base_url: "instit-quantilia/issuers",
    column_name: "name",
  },
  {
    name: "ticker_contains",
    label: "Ticker",
    type: "input",
  },
  {
    name: "yahoo_ticker_contains",
    label: "Yahoo Ticker",
    type: "input",
  },
  {
    name: "code_contains",
    label: "Code",
    type: "input",
  },
  {
    name: "provider_compo_code_contains",
    label: "Provider Compo Code",
    type: "input",
  },
  {
    name: "provider_fixings_code_contains",
    label: "Provider Fixings Code",
    type: "input",
  },
  {
    name: "provider_config_contains",
    label: "Provider Config",
    type: "asyncAutocomplete",
    base_url: `${Clients.QTL}/provider_configs`,
    column_name: "code",
  },
  {
    name: "created_at",
    type: "dateRange",
    label: "Created at",
    from: { name: "created_at_from", label: "From" },
    to: { name: "created_at_to", label: "To" },
    col_span: 2,
  },
  {
    name: "is_benchmark",
    label: "Is Benchmark",
    type: "select",
    useTranslation: false,
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ],
  },
];

export const client = "qtl - referential";

export function FilterStrategy() {
  return (
    <FilterTable
      client={client}
      schema={filterStrategySchema}
      tableName={ClientTables.QTL_STRATEGIES}
      customBuildPath={() => `${client}/${ClientTables.QTL_STRATEGIES}`}
      fields={fields}
      columns={4}
    />
  );
}
